import { MenuFoldOutlined, MenuUnfoldOutlined, RocketTwoTone } from "@ant-design/icons";
import { Button, Menu, Tooltip } from "antd";
import Sider from "antd/es/layout/Sider";
import { compact, union } from "lodash";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { globalStores } from "../../GlobalStores";
import { PERMISSION_ACTION, PERMISSION_MODULE, ROUTES } from "../../common/constants";
import {
  AuditIcon,
  ChevronDownIcon,
  DashboardIcon,
  DocumentIcon,
  LinkIcon,
  RetailMediaIcon,
  SettingIcon,
  StudioIcon,
} from "../../common/icons";
import { getMenuItem } from "../../common/utils";
import { dashboardStores } from "../../pages/Dashboard/DashboardStores";
import "./sidenav.css";
import Plan from "../Plan";

// Keys of the submenus that (transitively) contain `key`; section groups are not submenus, so they are skipped
const findParentKeys = (nodes, key, parents = []) => {
  for (const node of nodes) {
    if (node.key === key) return parents;
    if (node.children) {
      const found = findParentKeys(node.children, key, node.type === "group" ? parents : [...parents, node.key]);
      if (found) return found;
    }
  }
  return null;
};

// Màn chi tiết → mục menu của màn danh sách tương ứng
const MENU_PARENT_ROUTES = {
  [ROUTES.profileView]: ROUTES.unifiedCustomerProfiles,
  [ROUTES.appEventDetail]: ROUTES.appEvents,
};

// Mục menu ứng với URL hiện tại (màn chi tiết thì lấy mục của màn danh sách)
const getActiveRoute = (pathname) => {
  const routeMatch = Object.values(ROUTES).find((item) => pathname.includes(item) && item !== "/");
  return MENU_PARENT_ROUTES[routeMatch] || routeMatch;
};

const Sidenav = observer(() => {
  const history = useHistory();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);

  const userInfo = toJS(dashboardStores.userInfo);
  const isRail = window.innerWidth >= 992 && globalStores.sidebarCollapse;

  const { t } = useTranslation();
  const planRef = useRef(null);

  useEffect(() => {
    if (location.pathname) {
      setSelectedKeys([getActiveRoute(location.pathname)]);
    }
  }, [location.pathname]);

  const onOpenChange = (keys) => {
    setOpenKeys([...keys]);
  };

  const items = useMemo(() => {
    const hasAny = (codes) => dashboardStores.hasAny(codes);

    const dashboardChildren = compact([
      hasAny([`${PERMISSION_MODULE.DASHBOARD_OVERVIEW}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Overview"), ROUTES.appOverview)
        : undefined,
      hasAny([`${PERMISSION_MODULE.DASHBOARD_EVENTS}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Event"), ROUTES.appEvents)
        : undefined,
      hasAny([`${PERMISSION_MODULE.DASHBOARD_DEVICES}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Device"), ROUTES.appDevices)
        : undefined,
      // process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION" &&
      // hasAny([`${PERMISSION_MODULE.DASHBOARD_CAMPAIGN_REPORT}-${PERMISSION_ACTION.READ}`])
      //   ? getMenuItem(t("Campaign Report"), ROUTES.campaignReport)
      //   : undefined,
      // process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION" &&
      // hasAny([`${PERMISSION_MODULE.DASHBOARD_CAMPAIGN_MANAGEMENT}-${PERMISSION_ACTION.READ}`])
      //   ? getMenuItem(t("Campaign Management"), ROUTES.campaignManagement)
      //   : undefined,
    ]);

    const deeplinkChildren = compact([
      hasAny([`${PERMISSION_MODULE.DEEPLINK_OVERVIEW}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Deep Link Overview"), ROUTES.overviewDeeplink)
        : undefined,
      hasAny([`${PERMISSION_MODULE.DEEPLINK_MANAGEMENT}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Deep Link"), ROUTES.appDeeplink)
        : undefined,
      hasAny([`${PERMISSION_MODULE.SHORTENLINK_MANAGEMENT}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Short link"), ROUTES.shortlink)
        : undefined,
    ]);

    const engagementChildren = compact([
      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_FLOWS}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Engagement Flow"), ROUTES.engagement)
        : undefined,

      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_JOURNEY}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Engagement Journey"), ROUTES.engagementPath)
        : undefined,

      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_PUSH_OPTION}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Notification Channel"), ROUTES.pushOption)
        : undefined,

      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_USER_SEGMENT}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("User Segment"), ROUTES.userSegment)
        : undefined,

      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_AB_TESTING}-${PERMISSION_ACTION.READ}`]) &&
      process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION"
        ? getMenuItem(t("Flag Config"), ROUTES.flagConfig)
        : undefined,

      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_AB_TESTING}-${PERMISSION_ACTION.READ}`]) &&
      process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION"
        ? getMenuItem(t("A/B Testing"), ROUTES.abTesting)
        : undefined,

      hasAny([`${PERMISSION_MODULE.EMAIL_MARKETING}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Email Marketing"), ROUTES.emailMarketing)
        : undefined,

      hasAny([`${PERMISSION_MODULE.NOTI_TOPIC_PUSH}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Topic Push"), ROUTES.topicPush)
        : undefined,

      hasAny([`${PERMISSION_MODULE.ENGAGEMENT_UNIFIED_CUSTOMER_PROFILES}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Unified Customer Profile"), ROUTES.unifiedCustomerProfiles)
        : undefined,
    ]);

    const retailerChildren = [
      getMenuItem(t("Statistics"), ROUTES.retailerStatistics),
      getMenuItem(t("Ad Inventory"), ROUTES.adInventory),
      getMenuItem(t("Ad Approval"), ROUTES.adApproval),
    ];

    const advertiserChildren = [
      getMenuItem(t("Statistics"), ROUTES.advertiserStatistics),
      getMenuItem(t("Ad Campaign"), ROUTES.adCampaign),
      getMenuItem(t("Product"), ROUTES.product),
      getMenuItem(t("Banner"), ROUTES.banner),
    ];

    const rmpChildren = compact([
      hasAny([`${PERMISSION_MODULE.RETAIL_MEDIA_RETAILER}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Retailer"), "retailer", undefined, retailerChildren, "group")
        : undefined,
      hasAny([`${PERMISSION_MODULE.RETAIL_MEDIA_ADVERTISER}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Advertiser"), "advertiser", undefined, advertiserChildren, "group")
        : undefined,
    ]);

    const settingChildren = compact([
      hasAny([`${PERMISSION_MODULE.SETTING_ACCOUNTS}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Account"), ROUTES.accounts)
        : undefined,
      hasAny([`${PERMISSION_MODULE.SETTING_ROLE}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Role"), ROUTES.role)
        : undefined,
      hasAny([`${PERMISSION_MODULE.SETTING_ACCESS_TOKEN}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(t("Access Token"), ROUTES.accessToken)
        : undefined,
    ]);

    const workspaceItems = compact([
      dashboardChildren.length
        ? getMenuItem(
            t("Dashboard"),
            "application",
            <span className="anticon">
              <DashboardIcon />
            </span>,
            dashboardChildren,
          )
        : undefined,
      deeplinkChildren.length
        ? getMenuItem(
            t("Link"),
            "link",
            <span className="anticon">
              <LinkIcon />
            </span>,
            deeplinkChildren,
          )
        : undefined,
      engagementChildren.length
        ? getMenuItem(
            t("Engagement Studio"),
            "engagement",
            <span className="anticon">
              <StudioIcon />
            </span>,
            engagementChildren,
          )
        : undefined,
      rmpChildren.length &&
      (process.env.REACT_APP_ENVIRONMENT !== "PRODUCTION" || userInfo?.partnerCode === "lynk_id_uat")
        ? getMenuItem(
            t("Retail Media"),
            "retail-media",
            <span className="anticon">
              <RetailMediaIcon />
            </span>,
            rmpChildren,
          )
        : undefined,
    ]);

    const systemItems = compact([
      settingChildren.length
        ? getMenuItem(
            t("Setting"),
            "setting",
            <span className="anticon">
              <SettingIcon />
            </span>,
            settingChildren,
          )
        : undefined,
      hasAny([`${PERMISSION_MODULE.AUDIT_LOG}-${PERMISSION_ACTION.READ}`])
        ? getMenuItem(
            t("Audit Log"),
            ROUTES.auditLog,
            <div>
              <span className="anticon">
                <AuditIcon />
              </span>
            </div>,
          )
        : undefined,
    ]);

    const resourceItems = [
      getMenuItem(
        <a href="https://developer.airflex.io/" target="_blank" rel="noreferrer">
          {t("Documents")}
        </a>,
        "documents",
        <div>
          <span className="anticon">
            <DocumentIcon />
          </span>
        </div>,
      ),
    ];

    const withPopupClass = (item) =>
      item.type !== "group" && item.children ? { ...item, popupClassName: "custom-menu-popup" } : item;

    // Permissions can hide a whole section, so only render the ones that still have entries
    return [
      [t("Workspace"), "section-workspace", workspaceItems],
      [t("System"), "section-system", systemItems],
      [t("Resources"), "section-resources", resourceItems],
    ]
      .filter(([, , sectionItems]) => sectionItems.length)
      .map(([label, key, sectionItems]) =>
        getMenuItem(label, key, undefined, sectionItems.map(withPopupClass), "group"),
      );
  }, [t, userInfo?.partnerCode]);

  const collapseSidebar = () => {
    if (!globalStores.sidebarCollapse && window.innerWidth < 992) {
      globalStores.setSidebarCollapse(true);
    }
  };

  const onMenuSelect = ({ key }) => {
    window.AirflexJS.logEvent("click_menu_sidebar", { key });
    if (Object.values(ROUTES).includes(key) && key !== location.pathname) history.push(key);
    setSelectedKeys([key]);
    collapseSidebar();
  };

  const submenuKeys = useMemo(
    () =>
      items
        .flatMap((section) => section.children)
        .filter((item) => item.children?.length)
        .map((item) => item.key),
    [items],
  );

  const defaultOpenKeys = useMemo(() => (submenuKeys.length < 3 ? submenuKeys : []), [submenuKeys]);

  useEffect(() => {
    setOpenKeys((prev) => union(defaultOpenKeys, prev));
  }, [defaultOpenKeys]);

  // Unfold the group that holds the active page, otherwise a reload leaves it hidden in a folded group
  useEffect(() => {
    const activeKey = selectedKeys[0];
    if (!activeKey) return;
    const firstSegment = activeKey.substring(1).split("/")[0];
    const parents = findParentKeys(items, activeKey) || (submenuKeys.includes(firstSegment) ? [firstSegment] : []);
    if (parents.length) setOpenKeys((prev) => union(prev, parents));
  }, [items, submenuKeys, selectedKeys]);

  return (
    <>
      {!globalStores.sidebarCollapse && window.innerWidth < 992 && (
        <div className="fixed inset-0 bg-[#00000073] z-[11]" onClick={() => globalStores.setSidebarCollapse(true)} />
      )}
      <Sider
        trigger={null}
        collapsible
        collapsed={globalStores.sidebarCollapse}
        width={256}
        collapsedWidth={window.innerWidth < 992 ? 0 : undefined}
        theme="light"
        breakpoint="lg"
        onBreakpoint={(broken) => globalStores.setSidebarCollapse(broken)}
        className="z-[11] custom-sider"
        style={window.innerWidth < 992 ? { position: "fixed" } : undefined}
      >
        <div className="flex-center shrink-0 !h-[72px] !pt-5 !mb-2" onClick={collapseSidebar}>
          <Link
            className="block text-main"
            key="logo"
            to={
              dashboardStores.hasAny([`${PERMISSION_MODULE.APPLICATION}-${PERMISSION_ACTION.READ}`])
                ? ROUTES.application
                : undefined
            }
          >
            {isRail ? (
              <img src="/assets/img/brand/logo_icon.png" width={40} alt="" />
            ) : (
              // logo.png has ~5px of transparent padding at the bottom, the top margin re-centres the visible mark
              <img className="!mt-[5px]" src="/assets/img/brand/logo.png" width={148} alt="" />
            )}
          </Link>
        </div>
        <Menu
          mode="inline"
          items={items}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          onClick={onMenuSelect}
          expandIcon={
            <span className="ant-menu-submenu-expand-icon sidenav-chevron">
              <ChevronDownIcon />
            </span>
          }
          className="custom-menu border-0 overflow-auto flex-1 min-h-0"
        />
        {!!userInfo?.is_growth_hacking &&
          (globalStores.sidebarCollapse ? (
            <div className="flex-center mt-2">
              <Tooltip title={t("Upgrade to Pro")} placement="right">
                <Button
                  className="bg-gradient-primary text-white rounded-lg !px-3"
                  onClick={() => planRef.current?.show()}
                >
                  <RocketTwoTone className="text-xl" twoToneColor="#5e72e4" />
                </Button>
              </Tooltip>
            </div>
          ) : (
            <div className="flex flex-col items-center !gap-3 rounded-xl border !border-indigo-100 bg-gradient-to-b from-indigo-50 to-white !px-4 !py-4 !mx-3 !my-3 shrink-0">
              <RocketTwoTone className="text-3xl" twoToneColor="#5e72e4" />
              <div className="text-center text-xs leading-relaxed text-[#475467]">
                {t("Want to get 10x more link quota and increased traffic limits?")}
              </div>
              <Button
                className="bg-gradient-primary text-white rounded-lg w-full font-medium !border-0"
                onClick={() => planRef.current?.show()}
              >
                {t("Upgrade to Pro")}
              </Button>
            </div>
          ))}
        {window.innerWidth >= 992 && (
          <div className="shrink-0 sidenav-footer">
            <Menu
              mode="inline"
              selectable={false}
              className="custom-menu collapse-menu border-0"
              onClick={() => globalStores.setSidebarCollapse(!globalStores.sidebarCollapse)}
              items={[
                {
                  key: "collapse-toggle",
                  icon: (
                    <div>
                      <span className="anticon p-px">
                        {globalStores.sidebarCollapse ? (
                          <MenuUnfoldOutlined className="!text-lg" />
                        ) : (
                          <MenuFoldOutlined className="!text-lg" />
                        )}
                      </span>
                    </div>
                  ),
                  label: globalStores.sidebarCollapse ? t("Expand") : t("Collapse"),
                },
              ]}
            />
          </div>
        )}
        <Plan ref={planRef} />
      </Sider>
    </>
  );
});

export default Sidenav;
